<template>
  <div class="col-12 col-xxl-6 bc-col">
    <div class="bc-card">
      <div class="bcc-header">
        <div class="bcc-text">
          <div class="bcc-title">{{ summary.name }}</div>
          <div class="bcc-subtitle">{{ appliedText }}</div>
        </div>
        <FilterPopover
          title="Filter"
          popoverWidth="380px"
          :activeCount="activeCount"
          @open="onOpenFilter"
          @apply="onApplyFilter"
          @clear="onClearFilter"
        >
          <div class="col-6">
            <label class="filter-label">From</label>
            <input v-model="draft.from_date" type="date" class="form-control bcc-date">
          </div>
          <div class="col-6">
            <label class="filter-label">To</label>
            <input v-model="draft.to_date" type="date" class="form-control bcc-date">
          </div>
          <div class="col-12">
            <label class="filter-label">Compare By</label>
            <v-select
              v-model="draft.compare_by"
              :options="summary.compare_levels || []"
              label="name"
              :reduce="option => option.id"
              :clearable="false"
              :searchable="false"
            />
          </div>
          <div class="col-12">
            <label class="filter-label">Head Type</label>
            <v-select
              v-model="draft.head_type"
              :options="summary.head_types || []"
              label="name"
              :reduce="option => option.id"
              :clearable="false"
              :searchable="false"
            />
          </div>
        </FilterPopover>
      </div>
      <div v-if="intervalMessages.length" class="bcc-alert" role="alert">
        <div class="bcc-alert-title"><span><i class="fa-solid fa-triangle-exclamation"></i></span> These budgets use different intervals, so they cannot be compared.</div>
        <div v-for="message in intervalMessages" :key="message" class="bcc-alert-line">{{ message }}</div>
        <div class="bcc-alert-line">Change the dates so the range holds budgets of one interval.</div>
      </div>
      <div v-else-if="blockingText" class="bcc-empty">{{ blockingText }}</div>
      <template v-else>
        <div class="bcc-controls">
          <div v-if="! isItemHead" class="bcc-toggle" role="group">
            <button
              v-for="view in views"
              :key="view.key"
              type="button"
              class="bcc-toggle-btn"
              :class="{ 'bcc-toggle-active': view.key === selectedView }"
              @click="onSelectView(view.key)"
            >{{ view.name }}</button>
          </div>
          <div v-if="showMeasureSwitch" class="bcc-toggle" role="group">
            <button
              v-for="measure in MEASURES"
              :key="measure.key"
              type="button"
              class="bcc-toggle-btn"
              :class="{ 'bcc-toggle-active': measure.key === selectedMeasure }"
              @click="onSelectMeasure(measure.key)"
            >{{ measure.name }}</button>
          </div>
          <div v-if="! chartEmptyText" class="bcc-legend">
            <span v-for="item in legendItems" :key="item.id" class="bcc-legend-item" :title="item.name">
              <span class="bcc-legend-swatch" :style="{ backgroundColor: item.color, borderColor: item.border }"></span>
              {{ limitText(item.name, LEGEND_LENGTH) }}
            </span>
          </div>
        </div>
        <div v-for="note in notes" :key="note" class="bcc-note"><span><i class="fa-solid fa-circle-info"></i></span> {{ note }}</div>
        <div v-if="chartEmptyText" class="bcc-empty">{{ chartEmptyText }}</div>
        <BudgetChart
          v-else-if="isBudgetView"
          :key="chartKey"
          :rows="budgetRows"
          direction="horizontal"
          :favourableWhen="summary.favourable_when"
          :budgetMark="summary.budget_mark"
          :showUsed="summary.show_used"
          :chartId="chartKey"
        />
        <BudgetComparisonChart
          v-else
          :key="`${chartKey}_${calculatedAt}`"
          :series="series"
          :measure="activeMeasure"
          :chartId="chartKey"
        />
        <div class="table-responsive bcc-table-wrap">
          <table class="table align-middle bc-table bcc-table">
            <thead>
              <tr>
                <th class="text-start">Budget</th>
                <th v-if="! isItemHead" class="text-end">Periods Reached</th>
                <th v-if="! isItemHead" class="text-end">Budget To Date</th>
                <th v-if="! isItemHead" class="text-end">Actual To Date</th>
                <th v-if="! isItemHead" class="text-end">Variance</th>
                <th v-if="isItemHead" class="text-end">Total Budget</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in series" :key="item.id">
                <td class="bc-name bcc-budget-cell">
                  <div class="bcc-table-name">
                    <span class="bcc-legend-swatch bcc-table-swatch" :style="{ backgroundColor: item.color, borderColor: item.color }"></span>
                    <span>{{ item.name }}</span>
                  </div>
                  <div class="bcc-period">{{ periodText(item) }}</div>
                </td>
                <template v-if="! isItemHead">
                  <td class="text-end bc-amount bcc-figure">
                    <div>{{ reachedText(item) }}</div>
                    <div class="bcc-meter"><span class="bcc-meter-fill" :style="{ width: `${reachedPercent(item)}%`, backgroundColor: item.color }"></span></div>
                  </td>
                  <td class="text-end bc-amount bcc-figure" :class="{ 'bcc-muted': ! hasStarted(item) }">{{ hasStarted(item) ? formatAmount(item.to_date.budget) : '-' }}</td>
                  <td class="text-end bc-amount bcc-figure" :class="{ 'bcc-muted': ! hasStarted(item) }">{{ hasStarted(item) ? formatAmount(item.to_date.actual) : 'Not started' }}</td>
                  <td class="text-end bc-amount">
                    <span v-if="hasStarted(item)" class="bcc-variance" :class="varianceClass(item)">{{ varianceText(item) }}</span>
                    <span v-else class="bcc-muted">-</span>
                  </td>
                </template>
                <td v-if="isItemHead" class="text-end bc-amount">{{ formatAmount(item.total_budget) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import figureFormatter from '@/services/utils/figureFormatter'
import useDate from '@/services/utils/day'
import BudgetChart from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/BudgetChart'
import BudgetComparisonChart from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/BudgetComparisonChart'
import { ACTUAL_COLOR, BUDGET_BORDER_COLOR, BUDGET_COLOR, assignSeriesColors, formatAmount, isPeriodInRange, isPeriodPartial, monthsCovered, periodSpan, roundAmount, statusOf, varianceOf } from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/budgetFigures'

const LINE_LEVEL = 'line'
const BUDGET_LEVEL = 'budget'
const ACCOUNT_HEAD = 'account'
const ITEM_HEAD = 'item'
const PERIOD_VIEW = 'period'
const BUDGET_VIEW = 'budget'
const BUDGET_MEASURE = 'budget'
const ACTUAL_MEASURE = 'actual'
const MEASURES = [
  { key: BUDGET_MEASURE, name: 'Budget' },
  { key: ACTUAL_MEASURE, name: 'Actual' }
]
const MAX_SERIES = 6
const LEGEND_LENGTH = 28
const FILTER_KEYS = ['from_date', 'to_date', 'compare_by', 'head_type']

const props = defineProps({
  statisticsItem: Object,
  itemKey: String,
  page: Object
})

const { limitText } = figureFormatter()
const { formatToDdMmmYyyy } = useDate()

const summary = computed(() => props.statisticsItem[props.itemKey].data || {})
const calculatedAt = computed(() => props.statisticsItem[props.itemKey].calculated_at)
const views = computed(() => summary.value.views || [])
const allBudgets = computed(() => Object.values(summary.value.budgets || {}))

const asOfYear = computed(() => Number(String(summary.value.as_of || '').slice(0, 4)) || new Date().getFullYear())

const defaultFilters = () => ({
  from_date: `${asOfYear.value - 1}-01-01`,
  to_date: `${asOfYear.value + 1}-12-31`,
  compare_by: LINE_LEVEL,
  head_type: ACCOUNT_HEAD
})

const applied = ref(defaultFilters())
const draft = ref(defaultFilters())
const selectedView = ref(PERIOD_VIEW)
const selectedMeasure = ref(BUDGET_MEASURE)

const onOpenFilter = () => {
  draft.value = { ...applied.value }
}

const onApplyFilter = () => {
  applied.value = { ...draft.value }
}

const onClearFilter = () => {
  applied.value = defaultFilters()
  draft.value = defaultFilters()
}

const onSelectView = (key) => {
  selectedView.value = key
}

const onSelectMeasure = (key) => {
  selectedMeasure.value = key
}

watch(asOfYear, onClearFilter)

const activeCount = computed(() => {
  const defaults = defaultFilters()
  return FILTER_KEYS.filter(key => applied.value[key] !== defaults[key]).length
})

const isItemHead = computed(() => applied.value.head_type === ITEM_HEAD)
const isBudgetView = computed(() => ! isItemHead.value && selectedView.value === BUDGET_VIEW)
const showMeasureSwitch = computed(() => ! isItemHead.value && selectedView.value === PERIOD_VIEW)
const activeMeasure = computed(() => (isItemHead.value ? BUDGET_MEASURE : selectedMeasure.value))

const byStartThenName = (first, second) => first.start_date.localeCompare(second.start_date) || first.name.localeCompare(second.name)

const headOf = (budget) => {
  if (! budget.heads) return null
  return budget.heads[applied.value.head_type] || null
}

const rangeRows = (budget) => {
  const head = headOf(budget)
  if (! head) return []
  return head.rows.filter(row => isPeriodInRange(row.label, applied.value.from_date, applied.value.to_date))
}

const sumOf = (rows, key) => roundAmount(rows.reduce((total, row) => total + row[key], 0))

const cutTotals = (rows) => {
  const reached = rows.filter(row => row.actual !== null)
  return {
    to_date: { budget: sumOf(reached, BUDGET_MEASURE), actual: sumOf(reached, ACTUAL_MEASURE) },
    total_budget: sumOf(rows, BUDGET_MEASURE)
  }
}

const levelBudgets = computed(() => allBudgets.value
  .filter(budget => budget.level === applied.value.compare_by)
  .sort(byStartThenName))

const isValidRange = computed(() => {
  if (! applied.value.from_date || ! applied.value.to_date) return false
  return applied.value.from_date <= applied.value.to_date
})

const inRange = computed(() => levelBudgets.value
  .filter(budget => budget.start_date <= applied.value.to_date && budget.end_date >= applied.value.from_date)
  .filter(budget => rangeRows(budget).some(row => row.budget || row.actual)))

const intervalGroups = computed(() => {
  const groups = {}
  inRange.value.forEach(budget => {
    groups[budget.interval] = groups[budget.interval] || { name: budget.interval_name, budgets: [] }
    groups[budget.interval].budgets.push(budget)
  })
  return Object.values(groups)
})

const hasMixedIntervals = computed(() => intervalGroups.value.length > 1)
const isOverLimit = computed(() => inRange.value.length > MAX_SERIES)

const hasManyLines = computed(() => new Set(inRange.value.map(budget => budget.line_name)).size > 1)

const displayName = (budget) => {
  if (budget.level === LINE_LEVEL || ! hasManyLines.value) return budget.name
  return `${budget.line_name} · ${budget.name}`
}

const dateError = computed(() => {
  if (! applied.value.from_date || ! applied.value.to_date) return 'Select the From and To dates.'
  return isValidRange.value ? '' : 'From date must be on or before To date.'
})

const noBudgetText = computed(() => {
  if (! allBudgets.value.length) return 'No approved budget'
  return isItemHead.value ? 'No Item budget in this date range' : 'No approved budget falls within this date range'
})

const overLimitText = computed(() => {
  const hint = applied.value.compare_by === BUDGET_LEVEL ? ' Shorten the dates or compare by Master Budget Line.' : ' Shorten the dates.'
  return `This range has ${inRange.value.length} budgets. The chart shows up to ${MAX_SERIES}.${hint}`
})

const intervalMessages = computed(() => {
  if (dateError.value || ! hasMixedIntervals.value) return []
  return intervalGroups.value.map(group => `${group.name}: ${group.budgets.map(displayName).join(', ')}`)
})

const blockingText = computed(() => {
  if (dateError.value) return dateError.value
  if (! inRange.value.length) return noBudgetText.value
  return isOverLimit.value ? overLimitText.value : ''
})

const colors = computed(() => {
  if (blockingText.value || hasMixedIntervals.value) return {}
  return assignSeriesColors(levelBudgets.value.map(budget => budget.id), inRange.value.map(budget => budget.id))
})

const series = computed(() => {
  if (blockingText.value || hasMixedIntervals.value) return []
  return inRange.value.map(budget => {
    const head = headOf(budget)
    const rows = rangeRows(budget)
    return {
      ...budget,
      ...head,
      ...(rows.length < head.rows.length ? cutTotals(rows) : {}),
      rows,
      name: displayName(budget),
      color: colors.value[budget.id]
    }
  })
})

const hasStarted = (item) => item.rows.some(row => row.actual !== null)

const reachedCount = (item) => item.rows.filter(row => row.actual !== null).length

const reachedText = (item) => `${reachedCount(item)} of ${item.rows.length}`

const reachedPercent = (item) => (item.rows.length ? reachedCount(item) / item.rows.length * 100 : 0)

const periodText = (item) => periodSpan(item.rows)

const varianceFor = (item) => varianceOf(item.to_date.budget, item.to_date.actual, summary.value.favourable_when)

const varianceClass = (item) => {
  const variance = varianceFor(item)
  if (variance < 0) return 'bcc-variance-adverse'
  return variance > 0 ? 'bcc-variance-favourable' : ''
}

const varianceText = (item) => {
  const variance = varianceFor(item)
  return `${statusOf(variance).arrow} ${formatAmount(variance)}`.trim()
}

const budgetRows = computed(() => series.value.filter(hasStarted).map(item => ({
  label: item.name,
  budget: item.to_date.budget,
  actual: item.to_date.actual
})))

const hasPartialPeriod = (item) => {
  const fromDate = applied.value.from_date > item.start_date ? applied.value.from_date : item.start_date
  const toDate = applied.value.to_date < item.end_date ? applied.value.to_date : item.end_date
  return item.rows.some(row => isPeriodPartial(row.label, fromDate, toDate))
}

const notes = computed(() => {
  const list = []
  if (series.value.length === 1) list.push('Only one budget in this range')
  if (new Set(series.value.map(item => monthsCovered(item.rows))).size > 1) list.push('These budgets cover different lengths, so totals are not like for like')
  if (series.value.some(hasPartialPeriod)) list.push('Some periods are only partly inside the dates and are shown whole')
  return list
})

const hasMeasureValues = computed(() => series.value.some(item => item.rows.some(row => row[activeMeasure.value])))

const chartEmptyText = computed(() => {
  if (isBudgetView.value && ! budgetRows.value.length) return 'No period has started yet'
  if (isBudgetView.value) return ''
  if (activeMeasure.value === ACTUAL_MEASURE && ! series.value.some(hasStarted)) return 'No period has started yet'
  return hasMeasureValues.value ? '' : `No ${activeMeasure.value} yet`
})

const legendItems = computed(() => {
  if (isBudgetView.value) {
    return [
      { id: BUDGET_MEASURE, name: 'Budget', color: BUDGET_COLOR, border: BUDGET_BORDER_COLOR },
      { id: ACTUAL_MEASURE, name: 'Actual', color: ACTUAL_COLOR, border: ACTUAL_COLOR }
    ]
  }
  return series.value.map(item => ({ id: item.id, name: item.name, color: item.color, border: item.color }))
})

const chartKey = computed(() => `${props.itemKey}_${applied.value.from_date}_${applied.value.to_date}_${applied.value.head_type}_${activeMeasure.value}_${isBudgetView.value ? BUDGET_VIEW : PERIOD_VIEW}_${series.value.map(item => item.id).join('_')}`)

const appliedText = computed(() => {
  const parts = []
  if (isValidRange.value) parts.push(`${formatToDdMmmYyyy(applied.value.from_date)} to ${formatToDdMmmYyyy(applied.value.to_date)}`)
  parts.push(isItemHead.value ? 'Item' : 'Account')
  if (intervalGroups.value.length === 1) parts.push(intervalGroups.value[0].name)
  if (inRange.value.length) parts.push(`${inRange.value.length} ${inRange.value.length === 1 ? 'budget' : 'budgets'}`)
  if (summary.value.as_of) parts.push(`As of ${formatToDdMmmYyyy(summary.value.as_of)}`)
  return parts.join(' · ')
})
</script>

<style>
section.dashboard-section:has(.bcc-header) {
  overflow: visible;
}
</style>

<style scoped src="./budget/budgetCard.css"></style>

<style scoped>
.bcc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f0f1f4;
}
.bcc-text {
  flex: 1 1 auto;
  min-width: 0;
}
.bcc-title {
  font-size: 15px;
  font-weight: 700;
  color: #2b2b2b;
}
.bcc-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: #8a8894;
}
.bcc-date {
  height: 38px;
  font-size: 13px;
}
.bcc-alert {
  padding: 12px 14px;
  border: 1px solid #fedf89;
  border-radius: 8px;
  background: #fffaeb;
  font-size: 13px;
  color: #b54708;
}
.bcc-alert-title {
  margin-bottom: 6px;
  font-weight: 600;
}
.bcc-alert-line {
  margin-top: 2px;
}
.bcc-note {
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f5f6fa;
  font-size: 12px;
  color: #667085;
}
.bcc-legend-swatch {
  border-radius: 50%;
}
.bcc-table-wrap {
  margin-top: 14px;
}
.bcc-table thead th {
  padding: 10px 14px !important;
  background-color: transparent !important;
  color: #667085;
}
.bcc-table tbody td {
  padding: 12px 14px !important;
  border-bottom-color: #f0f1f4;
}
.bcc-table tbody tr:last-child td {
  border-bottom-width: 0;
}
.bcc-table tbody tr:hover td {
  background-color: #fafbfc;
}
.bcc-figure {
  font-weight: 500;
}
.bcc-muted {
  font-weight: 500;
  color: #667085;
}
.bcc-meter {
  width: 72px;
  height: 4px;
  margin: 6px 0 0 auto;
  overflow: hidden;
  border-radius: 999px;
  background: #eef0f4;
}
.bcc-meter-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}
.bcc-variance {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
}
.bcc-variance-favourable {
  background: #e6f6f3;
  color: #0f766e;
}
.bcc-variance-adverse {
  background: #fdecec;
  color: #c53030;
}
.bcc-budget-cell {
  min-width: 190px;
}
.bcc-table-name {
  display: flex;
  align-items: center;
  gap: 6px;
}
.bcc-table-swatch {
  flex: none;
}
.bcc-period {
  margin: 2px 0 0 16px;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  color: #667085;
}
@media (max-width: 576px) {
  .bcc-header {
    position: relative;
  }
  .bcc-header :deep(.filter-pop-wrap) {
    position: static;
  }
  .bcc-header :deep(.filter-pop) {
    left: 0;
    right: 0;
    width: 100% !important;
  }
}
</style>
